/* =====================================================================================
   Editing a record that has already been written, with the change recorded.

   Field observations and interaction notes are written quickly, often on a phone between
   meetings, and they are frequently wrong in small ways: a name misheard, a date a day out, a
   figure that turns out to be the wrong year. Until now the only remedy was to add a second
   record contradicting the first, which leaves both on the page and the reader to guess.

   THE AUDIT IS NOT BUILT HERE, deliberately. Every table this edits carries the zz_audit
   trigger from migration 0001, which writes actor, action, before row and after row into
   app.audit_log on the database side. That is the only place it can be recorded honestly: a
   trail assembled by the client is a trail the client can decline to write. This component
   therefore does not attempt to log anything, and the history it shows is read back from that
   table rather than from anything it kept itself.

   WHAT IT REFUSES TO DO. It will not delete. An observation that turned out to be wrong is
   itself informative, and the retraction pattern used for notices applies here too: correcting
   the text leaves the record and its history intact, which is what makes the correction
   trustworthy.
   ===================================================================================== */

import { useEffect, useState } from 'react';
import { useDialog } from '../lib/ui';
import { BACKEND } from '../lib/db';
import './createforms.css';

export interface EditField {
  key: string;
  label: string;
  type?: 'text' | 'textarea' | 'date' | 'select';
  options?: { value: string; label: string }[];
  hint?: string;
}

export interface AuditRow {
  occurred_at: string;
  action: string;
  actor: string | null;
  changed: string[] | null;
}

/** Reads the trail the database kept, for one record. */
export async function fetchHistory(table: string, recordId: string): Promise<AuditRow[]> {
  if (BACKEND !== 'supabase') return [];
  const { supabase, nameOf } = await import('../lib/supabase');
  const [schema, name] = table.split('.');
  const { data, error } = await supabase().schema('app').from('audit_log')
    .select('occurred_at, action, actor_member, actor_email, changed')
    .eq('schema_name', schema).eq('table_name', name).eq('record_id', recordId)
    .order('occurred_at', { ascending: false }).limit(30);
  if (error) throw new Error(error.message);
  const who = await nameOf();
  return (data ?? []).map((r: Record<string, unknown>) => ({
    occurred_at: r.occurred_at as string,
    action: r.action as string,
    actor: who(r.actor_member as string) ?? (r.actor_email as string) ?? null,
    changed: (r.changed as string[]) ?? null,
  }));
}

export function EditRecord({
  title, table, recordId, fields, value, onSave, onClose,
}: {
  title: string;
  /** Qualified name, so the history can be read back for exactly this row. */
  table: string;
  recordId: string;
  fields: EditField[];
  value: Record<string, unknown>;
  onSave: (patch: Record<string, unknown>) => Promise<void>;
  onClose: () => void;
}) {
  const ref = useDialog(onClose);
  const [f, setF] = useState<Record<string, unknown>>(value);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [history, setHistory] = useState<AuditRow[]>([]);
  const [showHistory, setShowHistory] = useState(false);

  useEffect(() => {
    fetchHistory(table, recordId).then(setHistory).catch(() => setHistory([]));
  }, [table, recordId]);

  /* Only what actually changed goes back. Sending the whole record would make every save look
     like an edit of every field in the audit trail, which destroys the value of having one. */
  const patch = Object.fromEntries(
    Object.entries(f).filter(([k, v]) => v !== value[k]),
  );
  const dirty = Object.keys(patch).length > 0;

  async function save() {
    setBusy(true); setErr(null);
    try { await onSave(patch); onClose(); }
    catch (e) { setErr(e instanceof Error ? e.message : String(e)); }
    finally { setBusy(false); }
  }

  return (
    <>
      <div className="cf-scrim" onClick={onClose} />
      <div className="cf" role="dialog" aria-modal="true" aria-label={title} ref={ref}
           onClick={(e) => e.stopPropagation()}>
        <header className="cf-head">
          <h2>{title}</h2>
          <button className="icon-btn" onClick={onClose} aria-label="Close">✕</button>
        </header>
        <div className="cf-body">
          {fields.map((fl) => (
            <label className="cf-field" key={fl.key}>
              <span>{fl.label}</span>
              {fl.type === 'textarea' ? (
                <textarea rows={3} value={(f[fl.key] as string) ?? ''}
                          onChange={(e) => setF({ ...f, [fl.key]: e.target.value })} />
              ) : fl.type === 'select' ? (
                <select value={(f[fl.key] as string) ?? ''}
                        onChange={(e) => setF({ ...f, [fl.key]: e.target.value })}>
                  {(fl.options ?? []).map((o) =>
                    <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : (
                <input type={fl.type === 'date' ? 'date' : 'text'}
                       value={(f[fl.key] as string) ?? ''}
                       onChange={(e) => setF({ ...f, [fl.key]: e.target.value })} />
              )}
              {fl.hint && <small className="cf-hint">{fl.hint}</small>}
            </label>
          ))}

          <p className="cf-note">
            Corrections are recorded. Who changed what, and when, is written by the database
            itself rather than by this screen, so the trail cannot be skipped.
          </p>

          {history.length > 0 && (
            <div className="cf-files">
              <button className="btn ghost sm" onClick={() => setShowHistory(!showHistory)}>
                {showHistory ? 'Hide' : 'Show'} history ({history.length})
              </button>
              {showHistory && (
                <ul className="cf-history">
                  {history.map((h, i) => (
                    <li key={i}>
                      <strong>{h.action}</strong>
                      {h.actor ? ` by ${h.actor}` : ''}
                      {' · '}
                      {new Date(h.occurred_at).toLocaleString('en-GB')}
                      {h.changed?.length ? ` · ${h.changed.join(', ')}` : ''}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {err && <p className="composer-err" role="alert">{err}</p>}
          <div className="cf-actions">
            <button className="btn-cancel" onClick={onClose} disabled={busy}>Cancel</button>
            <button className="btn-save" onClick={save} disabled={busy || !dirty}>
              {busy ? 'Saving' : dirty ? 'Save correction' : 'No changes'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
